import { faEnvelopeOpenDollar, faExternalLink, faFileExport } from "@fortawesome/pro-solid-svg-icons"
import { gold } from "@ant-design/colors"
import { Flex, Popconfirm } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ThemableButton from "../../Items/ThemableButton"
import { InvoiceDetail, InvoiceSummary, isInvoiceIndependentlyPayable } from "../../../domain/invoicing/invoice"
import { useDeleteInvoiceMutation, useEmailInvoiceMutation, useSyncInvoiceToQuickBooksMutation } from "../../../domain/invoicing/invoicingQueries"
import IconAndTextButton from "../../IconAndTextButton"
import { useContext, useState } from "react"
import ExportInvoiceModal from "./ExportInvoiceModal"
import ExportConfirmationModal from "../ExportConfirmationModal"
import DeleteInvoiceButton from "./DeleteInvoiceButton"
import ManualMarkNotPaidButton from "./ManualMarkNotPaidButton"
import ViewReceiptButton from "./ViewReceiptButton"
import SendInvoiceButton from "../SendInvoice/SendInvoiceButton"
import PayInvoiceButton from "../PayInvoice/PayInvoiceButton"
import SyncInvoiceButton from "./SyncInvoiceButton"
import { shouldShowSyncInvoiceButton } from "../../../domain/invoicing/quickBooksTrackingModel"
import { AuthenticationContext } from "../../../AuthenticationContext"

type Props = {
  invoiceSummary: InvoiceSummary
  detail: InvoiceDetail
  preventExports: boolean
}

const InvoiceControls = ({ invoiceSummary, detail, preventExports }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const emailInvoiceMutation = useEmailInvoiceMutation()
  const deleteInvoiceMutation = useDeleteInvoiceMutation()
  const syncInvoiceMutation = useSyncInvoiceToQuickBooksMutation()
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportConfirmationUrl, setExportConfirmationUrl] = useState<string | null>(null)
  const isTenantConnectedToQuickBooks = currentUser?.isTenantConnectedToQuickBooks === true

  const isInvoicePaid = invoiceSummary.balance < invoiceSummary.total
  const isExported = detail.invoice.qboInvoiceId.length > 0
  const controlsDisabled = emailInvoiceMutation.isLoading || deleteInvoiceMutation.isLoading || syncInvoiceMutation.isLoading
  const emailTo = (detail.quickBooksInvoice?.emailAddress ?? '').length > 0
    ? <strong>{detail.quickBooksInvoice?.emailAddress}</strong>
    : 'the customer'
  
  const isQuickBooks = invoiceSummary.invoicingSystem === 'quickBooks'
  const showOpenInQbo = isQuickBooks && isExported
  const showExportToQbo = isQuickBooks && !isExported
  const showEmailViaQbo = isQuickBooks && isExported
  const showPayInvoiceButton = invoiceSummary.invoicingSystem === 'synkedUp' && invoiceSummary.balance > 0 && isInvoiceIndependentlyPayable(invoiceSummary)
  const showSendInvoiceButton = invoiceSummary.invoicingSystem === 'synkedUp' && invoiceSummary.balance > 0
  const showSyncInvoiceButton = shouldShowSyncInvoiceButton(isTenantConnectedToQuickBooks, invoiceSummary)

  return <div style={{ fontSize: 16, textAlign: 'right', padding: '12px' }}>
    <Flex gap={10} justify="flex-end">
      {showSyncInvoiceButton && <SyncInvoiceButton
        disabled={controlsDisabled}
        isLoading={syncInvoiceMutation.isLoading}
        onSync={() => syncInvoiceMutation.mutate(detail.invoice.id)}
      />}
      <ManualMarkNotPaidButton
        summary={invoiceSummary}
        disabled={controlsDisabled}
      />
      <ViewReceiptButton
        detail={detail}
        summary={invoiceSummary}
      />
      {showSendInvoiceButton && <SendInvoiceButton
        invoiceId={detail.invoice.id}
        controlsDisabled={controlsDisabled}
      />}
      {showPayInvoiceButton && <PayInvoiceButton
        invoiceId={detail.invoice.id}
        disabled={controlsDisabled}
      />}
      {showOpenInQbo && <IconAndTextButton
        type="primary"
        icon={faExternalLink}
        text="Open in QuickBooks Online"
        disabled={detail.qboInvoiceUrl.length === 0 || controlsDisabled}
        onClick={() => window.open(detail.qboInvoiceUrl, "_blank")}
      />}
      {showExportToQbo && <IconAndTextButton
        type="primary"
        icon={faFileExport}
        text="Export to QuickBooks"
        disabled={preventExports || controlsDisabled}
        onClick={() => setShowExportModal(true)}
      />}
      {showEmailViaQbo &&
          <Popconfirm
            title={<span>Have QuickBooks email this invoice to {emailTo}?</span>}
            okText="Send"
            onConfirm={() => emailInvoiceMutation.mutate(detail.invoice.id)}
          >
            <ThemableButton
              type="primary"
              icon={<FontAwesomeIcon icon={faEnvelopeOpenDollar} />}
              colorPrimary={gold.primary}
              disabled={controlsDisabled}
              loading={emailInvoiceMutation.isLoading}
            >
              Email Invoice to Customer
            </ThemableButton>
          </Popconfirm>
        }
        <DeleteInvoiceButton
          invoice={detail.invoice}
          isInvoicePaid={isInvoicePaid}
          controlsDisabled={controlsDisabled}
          isLoading={deleteInvoiceMutation.isLoading}
          onDelete={(deleteFromQuickBooks) => deleteInvoiceMutation.mutate({
            invoiceId: detail.invoice.id,
            deleteFromQuickBooks,
            projectId: detail.invoice.jobId
          })}
        />
    </Flex>
    <ExportInvoiceModal
      isOpen={showExportModal}
      onClose={() => setShowExportModal(false)}
      onExported={(qboInvoiceUrl) => {
        setShowExportModal(false)
        setExportConfirmationUrl(qboInvoiceUrl)
      }}
      invoice={detail.invoice}
    />
    <ExportConfirmationModal
      isOpen={exportConfirmationUrl !== null}
      onClose={() => setExportConfirmationUrl(null)}
      qboInvoiceUrl={exportConfirmationUrl ?? ''}
    />
  </div>
}

export default InvoiceControls